import { EntityIcon, EntityIconPlayable } from "./EntityIcon";
import { EntityPreview } from "./EntityPreview";
import { EntityShortcut } from "./EntityShortcut";
import { EntityMetadata } from "./EntityMetadata";
import { EntityActions } from "./EntityActions";
import { ColumnConfig, RowConfig } from "App/common/BaseTableV2";
import { Entity, FileEntity, Track } from "@highnote/server/src/core/entities";
import { FileUpload } from "../useFiles";
import { Action } from "App/common/Actions";
import { EntityComments } from "./EntityComments";
import { EntityCTA } from "./EntityCTA";
import { EntityCreatedAt } from "./EntityCreatedAt";
import { Confirmator } from "App/common/useConfirmation";
import { ViewingSpaceId } from "App/modules/viewingSpace";

export const DEFAULT_COLUMNS: Record<string, ColumnConfig> = {
  ICON: {
    name: "Icon",
    id: "icon",
    Component: EntityIcon,
  },
  ICON_PLAYABLE: {
    name: "IconPlayable",
    id: "icon-playable",
    Component: EntityIconPlayable,
  },
  PREVIEW: {
    name: "Preview",
    id: "preview",
    Component: EntityPreview,
  },
  SHORTCUT: {
    name: "Shortcut",
    id: "shortcut",
    Component: EntityShortcut,
    showOnHover: true,
  },
  COMMENTS: {
    name: "# Comments",
    id: "comment-count",
    Component: EntityComments,
  },
  COMMENTS_MOBILE: {
    name: "# Comments",
    id: "comment-count-mobile",
    Component: EntityComments,
  },
  // Generalized right-side indicator / CTA column: per entity type renders the
  // comment facepile/button (tracks), a go-to-space caret (spaces, below M), or
  // an open-in-new-tab arrow (url/file attachments).
  CTA: {
    name: "CTA",
    id: "cta",
    Component: EntityCTA,
  },
  CREATED_AT: {
    name: "Created At",
    id: "created-at",
    Component: EntityCreatedAt,
  },
  METADATA: {
    name: "Metadata",
    id: "metadata",
    Component: EntityMetadata,
  },
  ACTIONS: {
    name: "Actions",
    id: "actions",
    Component: EntityActions,
  },
};

export const UTILITY_COLUMNS = ["drag-handle", "check"];
export const MAIN_CONTENT_COLUMNS = [
  DEFAULT_COLUMNS.ICON.id,
  DEFAULT_COLUMNS.ICON_PLAYABLE.id,
  DEFAULT_COLUMNS.PREVIEW.id,
  DEFAULT_COLUMNS.CREATED_AT.id,
  DEFAULT_COLUMNS.METADATA.id,
  DEFAULT_COLUMNS.COMMENTS.id,
  DEFAULT_COLUMNS.COMMENTS_MOBILE.id,
  DEFAULT_COLUMNS.CTA.id,
];

export const ACTION_COLUMNS = [
  DEFAULT_COLUMNS.SHORTCUT.id,
  DEFAULT_COLUMNS.ACTIONS.id,
];

export enum ENTITY_TYPE {
  SPACE = "SPACE",
  TRACK = "TRACK",
  TRACK_VERSION = "TRACK_VERSION",
  FILE = "FILE",
  UPLOAD = "UPLOAD",
  SEPARATOR = "SEPARATOR",
}

export type Version = FileEntity & {
  isVersion: true;
  index: number;
  colorId: number;
  isDefaultVersion: boolean;
  commentCount?: number;
  track?: Track;
};

export type SpaceTrack = Track & {
  isSpaceTrack: true;
  index: number;
};

// Selectors for which metadata fields EntityPreview renders, in order.
// Caller-controlled so different surfaces (search, library, move-to) can
// each pick what makes sense without forking the preview component.
//
// Use the ENTITY_PREVIEW_METADATA constants below at call sites instead of
// raw strings (mirrors the DEFAULT_COLUMNS pattern).
export const ENTITY_PREVIEW_METADATA = {
  // entity-type icon (folder for spaces, music note for tracks, etc.)
  ICON: "icon",
  // avatar of the entity's creator (space.createdBy / track.createdBy)
  CREATED_BY_AVATAR: "createdByAvatar",
  // space.createdByName / track.artistName / track.createdByName
  CREATOR_NAME: "creatorName",
  // space.memberCount (only meaningful on public spaces)
  MEMBER_COUNT: "memberCount",
  // formatted creation date
  CREATED_AT: "createdAt",
  // SpaceDescription / TrackDescription
  DESCRIPTION: "description",
  // EntityMetadata (track count etc.)
  ITEM_COUNT: "itemCount",
  // host of a URL attachment (e.g. "example.com"), from file.url
  DOMAIN: "domain",
  // formatted size of a file attachment (e.g. "3.2 MB"), from file.size
  FILE_SIZE: "fileSize",
} as const;

export type PreviewMetadataField =
  (typeof ENTITY_PREVIEW_METADATA)[keyof typeof ENTITY_PREVIEW_METADATA];

export type EntityData = {
  id: string;
  entity: Entity | FileUpload | Version;
  type: ENTITY_TYPE;
  selected?: boolean;
  actions?: Action[] | JSX.Element;
  entireRowClickable?: boolean;
  // Override which metadata fields EntityPreview shows for this row, in
  // order. When omitted, the preview renders its default full metadata
  // (current behavior, no caller change required).
  entityPreviewMetadata?: PreviewMetadataField[];
  confirmOnClick?: (confirm: Confirmator) => Promise<unknown>;
  onConfirmCancel?: () => void;
  onClick?: () => void;
  /**
   * The space this row is being VIEWED from — the containing space of the
   * list the row sits in (the space page, an expanded space row, the
   * sidebar). Not the entity's own `spaceId` (a track's ORIGIN), and not
   * the route's space: a nested child-space row sits on the PARENT's page,
   * and a sidebar row sits under whatever route is open. This is what the
   * delete/remove guards send as the viewing `spaceId`. Absent for lists
   * with no containing space (library, search, move/add dialogs).
   */
  viewingSpaceId?: ViewingSpaceId;
};

export type EntityRowConfig = EntityData & RowConfig;
